/**
 * AI:
 * Status: 'in progress'
 * Type: Component
 * SubType: ObjectBaseModeComponent
 * Reason: Shared base of the lg.LogicMappingTable object component — loads the table, builds the four
 *         navigation sections (overview / entries / overrides / quick test), owns the child widgets and
 *         the withdrawal information (deactivate instead of delete).
 */
import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {MenuItem} from 'primeng/api';
import {ObjectBaseModeComponent} from '../../../../../../core/shared/object/mvs-object-base/object-base-mode.component';
import {MvsCoreService} from '../../../../../../core/shared/service/mvs-core.service';
import {WidgetData} from '../../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../../core/shared/misc/sorting';
import {DtoDetail} from '../../../../../../core/shared/dto/dto.detail';
import {MvsFormControlOverwrite} from '../../../../../../core/shared/logic/form/logic/mvs-form-control-overwrite';
import {MvsFormFieldAccessEnum} from '../../../../../../core/shared/logic/form/logic/mvs-form-field-access.enum';
import {WidgetToolbarCallbackInterface} from '../../../../../../core/shared/widget/widget-toolbar-callback.interface';
import {LogicMappingTableDto} from '../../../../model/dto/entity/logic-mapping-table.dto';
import {LogicMappingTableService} from '../../../../service/api/logic-mapping-table.service';
import {
    LG_MAPPING_TABLE_DEPENDENCY_COVERAGE_NOTE,
    LogicDependencyResultDto
} from '../../../../model/dto/api/logic-dependency-result.dto';
import {LogicDependentReferenceDto} from '../../../../model/dto/api/logic-dependent-reference.dto';
import {getMappingFallbackLabel, MappingFallbackEnum} from '../../../../enum/mapping-fallback.enum';

/**
 * Base class shared by the full and the side view of the `lg.LogicMappingTable` object component.
 *
 * <h2>Why this entity gets an object page and not a flat tab</h2>
 * `lg.LogicMappingTable` owns TWO child collections — `lg.LogicMappingTableEntry` and
 * `lg.LogicMappingTableOverride`. From two child collections upwards the Complex Configuration Entity
 * Rule applies: the config page shows a plain table, a row click opens this object component, and the
 * children live behind their own navigation sections. Flat tabs on the config page would be an
 * architectural error, not a matter of taste.
 *
 * <h2>What the subclasses decide</h2>
 * Everything except the navigation items: {@link loadSideBarMenuItems} is empty here and is filled in by
 * the full view (wide layout, own navigation rail) and by the side view (compact layout, the host renders
 * the rail from the emitted items).
 */
@Component({
    selector: 'mvs-lg-logic-mapping-table-object-component-base',
    template: '',
    standalone: false
})
export class LgLogicMappingTableObjectBaseComponent extends ObjectBaseModeComponent implements OnInit {

    /** Navigation action of the master-data section — the default section of this object component. */
    static readonly NAV_OVERVIEW: string = 'overview';

    /** Navigation action of the entries section (`lg.LogicMappingTableEntry`). */
    static readonly NAV_ENTRIES: string = 'entries';

    /** Navigation action of the partner-override section (`lg.LogicMappingTableOverride`). */
    static readonly NAV_OVERRIDES: string = 'overrides';

    /**
     * Navigation action of the quick-test section.
     *
     * Deliberately `quickTest` and not `test` / `testen`: both of those are taken by PERSISTED test cases
     * elsewhere in the lg module, and the logic object component uses `quickTest` for the very same kind
     * of section. Keeping one name across both object components makes a deep link behave the same way
     * wherever it is pasted.
     */
    static readonly NAV_QUICK_TEST: string = 'quickTest';

    override dto: LogicMappingTableDto;

    /** Typed CRUD service — needed for {@link loadDependents}, which is not a plain CRUD call. */
    mappingTableService: LogicMappingTableService;

    /** Master data of the table, rendered from the entity metadata. */
    widgetOverview: WidgetData;

    /** Table of `lg.LogicMappingTableEntry` rows belonging to this mapping table. */
    widgetEntries: WidgetData;

    /** Table of `lg.LogicMappingTableOverride` rows belonging to this mapping table. */
    widgetOverrides: WidgetData;

    /** Typed dependents of this table, loaded on demand by {@link loadDependents}. */
    dependents: LogicDependentReferenceDto[] = [];

    /** True while the dependency request is in flight. */
    dependentsLoading: boolean = false;

    /** True once a dependency request has completed — an empty list only means something after this. */
    dependentsLoaded: boolean = false;

    /** True when the dependency request failed; the empty list must then NOT be read as 'no dependents'. */
    dependentsLoadError: boolean = false;

    /**
     * The coverage limit of the dependency analysis, in German.
     *
     * Rendered next to every dependency result — including the empty one. The query follows typed
     * foreign keys only; `$ctx.lookupMapping('alias', ...)` inside a script body and direct Java calls in
     * the `ci` / `cm` / `cw` persist providers name their table by an alias STRING and stay invisible.
     * Without this note an incomplete search reads as an all-clear.
     */
    readonly dependencyCoverageNote: string = LG_MAPPING_TABLE_DEPENDENCY_COVERAGE_NOTE;

    @Output() onBreadcrumbItems: EventEmitter<MenuItem[]> = new EventEmitter();

    constructor(
        protected override route: ActivatedRoute,
        protected coreService: MvsCoreService) {
        super(route);
    }

    override ngOnInit() {

        // Navigation items must exist BEFORE the base class runs: its ngOnInit resolves the active
        // section from the navItem / sideNavItem query parameters against this list.
        this.loadSideBarMenuItems();

        super.ngOnInit();

        this.mappingTableService =
            <LogicMappingTableService><unknown>this.coreService.getCrudService('lg.LogicMappingTable');

        if (this.dto) {
            this.onObjectChanged();
        } else {
            this.loadData();
        }

        this.initialized = true;
    }

    /**
     * Loads the mapping table when the host did not hand one in.
     */
    loadData(): void {
        if (!this.mappingTableService || !this.objectIdentifier?.objectId) {
            return;
        }

        this.mappingTableService.get(this.objectIdentifier.objectId).subscribe((res: LogicMappingTableDto) => {
            this.dto = res;
            this.onObjectChanged();
        });
    }

    /**
     * Builds the navigation sections. Empty here — the full and the side view each provide their own cut.
     */
    loadSideBarMenuItems(): void {
    }

    /**
     * Called once the table is available. Builds the three widgets and publishes the breadcrumb.
     */
    onObjectChanged(): void {
        this.refreshWidgets();

        const item: MenuItem[] = [{
            icon: 'fa-regular fa-right-left',
            label: this.dto?.name,
            id: this.dto?.id as unknown as string
        }];

        this.onBreadcrumbItems.emit(item);
    }

    /**
     * (Re-)creates the master-data widget and both child widgets.
     */
    protected refreshWidgets(): void {

        const tableId: number = this.dto?.id ?? this.objectIdentifier?.objectId;

        if (!tableId) {
            return;
        }

        /** AI: Status: 'in progress', Type: Widget, SubType: Runtime,
            Reason: Master data of one lg.LogicMappingTable rendered from the entity metadata */
        this.widgetOverview = WidgetFactory.createWidgetEntityData(
            'lg.mapping.table.overview.data',
            'Mapping-Tabelle',
            'lg.LogicMappingTable',
            tableId
        );
        this.widgetOverview.functionCallbacks = this.createOverviewFieldLock();

        /** AI: Status: 'in progress', Type: Widget, SubType: Runtime,
            Reason: Table of lg.LogicMappingTableEntry rows of this mapping table (QL) */
        this.widgetEntries = WidgetFactory.createWidgetTableEntityQl(
            'lg.mapping.table.entries',
            'Einträge',
            'lg.LogicMappingTableEntry',
            'Keine Einträge vorhanden',
            [FilterCriteria.create('e.mappingTableId', FilterCriteria.cOperatorEqual, tableId)],
            [new Sorting('e.orderPriority', true)],
            true
        );
        this.widgetEntries.functionCallbacks = this.createChildDefaulting(tableId);

        /** AI: Status: 'in progress', Type: Widget, SubType: Runtime,
            Reason: Table of lg.LogicMappingTableOverride rows of this mapping table (QL) */
        this.widgetOverrides = WidgetFactory.createWidgetTableEntityQl(
            'lg.mapping.table.overrides',
            'Partner-Overrides',
            'lg.LogicMappingTableOverride',
            'Keine Partner-Overrides vorhanden',
            [FilterCriteria.create('e.mappingTableId', FilterCriteria.cOperatorEqual, tableId)],
            [new Sorting('e.sourceValue', true)],
            true
        );
        this.widgetOverrides.functionCallbacks = this.createChildDefaulting(tableId);
    }

    /**
     * Locks `targetObjectType` in the edit dialog of the master-data widget.
     *
     * The field has NO reader anywhere in the backend — a mapping always resolves to a string, never to
     * an entity. In the metadata-driven widget it nevertheless renders as an ordinary, editable foreign
     * key picker, so a user fills it in and believes the table is configured against that object type.
     * A note underneath the widget is too weak against that: it sits below the whole widget rather than
     * at the field.
     *
     * READ, deliberately not hidden: the column can already carry a value on existing rows, and hiding
     * it would make that value invisible while leaving it in the database.
     *
     * The data widget reads this callback for its EDIT dialog as well, not only for create
     * (`MvsWidgetDataComponent#handleEdit`), which is why the lock arrives through the same mechanism
     * the child widgets use for their foreign-key defaulting.
     *
     * KNOWN GAP: the widget's PER-FIELD edit dialog (`onEditFieldDialog`) builds its own
     * `MvsFormControlOverwrite.createHideAll(...)` and ignores this one. That path stays editable; it is
     * a framework-level behaviour this component cannot reach.
     */
    protected createOverviewFieldLock(): { onFunctionCreateNew: () => WidgetToolbarCallbackInterface } {
        return {
            onFunctionCreateNew: (): WidgetToolbarCallbackInterface => {
                const formControlOverwrite = new MvsFormControlOverwrite();
                formControlOverwrite.addField('targetObjectTypeDtoId', MvsFormFieldAccessEnum.READ);

                return {
                    dto: new DtoDetail(),
                    formControlOverwrite: formControlOverwrite
                };
            }
        };
    }

    /**
     * Pre-fills the `mappingTable` foreign key on a newly created child row and locks the field.
     *
     * Without this the `Neu` button produces a row with no table reference at all — the child widget
     * filters on that very foreign key, so the row would vanish the moment it is saved and the user would
     * have created an orphan without noticing.
     *
     * @param tableId id of the mapping table the child belongs to
     */
    protected createChildDefaulting(tableId: number): { onFunctionCreateNew: () => WidgetToolbarCallbackInterface } {
        return {
            onFunctionCreateNew: (): WidgetToolbarCallbackInterface => {
                const dto = new DtoDetail();
                dto['mappingTableDtoId'] = tableId;

                const formControlOverwrite = new MvsFormControlOverwrite();
                formControlOverwrite.addField('mappingTableDtoId', MvsFormFieldAccessEnum.READ);

                return {
                    dto: dto,
                    formControlOverwrite: formControlOverwrite
                };
            }
        };
    }

    /**
     * True only when this mapping table has been explicitly deactivated.
     *
     * `active` is a nullable Boolean with a Java initializer, so every row that existed before the
     * column was added carries NULL. The module-wide contract is that <b>NULL counts as ACTIVE</b> —
     * only an explicit `false` deactivates (`BooleanUtils.isNotFalse` in `LogicMappingService`). A
     * check written as `!this.dto?.active` would therefore mark every pre-existing mapping table as
     * deactivated, which is why this compares against `false` and nothing else.
     *
     * Deactivation is SELECTION semantics: the table can no longer be picked for a NEW assignment.
     * Existing configurations keep resolving against it unchanged — this flag has no effect on the
     * resolution path.
     */
    isTableInactive(): boolean {
        return this.dto?.active === false;
    }

    /**
     * The configured fallback behaviour, or `null` when the field is not set.
     *
     * The explicit null/undefined comparison is not decoration: the value arrives as an ORDINAL, and
     * ordinal `0` is `ERROR` — the hardest setting of all. A truthiness check would report exactly that
     * setting as `not configured`.
     */
    getFallbackBehaviour(): MappingFallbackEnum | null {
        const value = this.dto?.fallbackBehaviour;
        return value === null || value === undefined ? null : value;
    }

    /**
     * True when no fallback behaviour is configured.
     *
     * This is the single most common configuration mistake on a mapping table, which is why the overview
     * shows it as a warning instead of an empty field: an unset fallback behaves exactly like `Fehler`,
     * so the table throws on EVERY miss.
     */
    isFallbackBehaviourMissing(): boolean {
        return this.getFallbackBehaviour() === null;
    }

    /**
     * German label of the configured fallback behaviour, for the warning texts.
     *
     * Delegates to the shared helper on the enum rather than carrying its own mapping: a second label
     * table would drift away from the first one the moment a strategy is added or renamed.
     */
    getFallbackBehaviourLabel(): string {
        return getMappingFallbackLabel(this.getFallbackBehaviour());
    }

    /**
     * Loads everything that references this table through a typed foreign key.
     *
     * On failure the list is cleared AND {@link dependentsLoadError} is set, so the template can keep the
     * empty list apart from a failed request — showing `keine Abhängigkeiten` after an error would be the
     * worst possible outcome of this panel.
     */
    loadDependents(): void {

        const tableId: number = this.dto?.id ?? this.objectIdentifier?.objectId;

        if (!this.mappingTableService || !tableId) {
            return;
        }

        this.dependentsLoading = true;
        this.dependentsLoadError = false;

        this.mappingTableService.getDependents(tableId).subscribe({
            next: (res: LogicDependencyResultDto) => {
                this.dependents = res?.dependents ?? [];
                this.dependentsLoaded = true;
                this.dependentsLoading = false;
            },
            error: () => {
                this.dependents = [];
                this.dependentsLoaded = false;
                this.dependentsLoadError = true;
                this.dependentsLoading = false;
            }
        });
    }

    /**
     * German label for one dependent row: name, falling back to alias and then to the id.
     *
     * @param reference the dependent as returned by the dependency endpoint
     */
    getDependentLabel(reference: LogicDependentReferenceDto): string {
        if (reference?.name) {
            return reference.name;
        }
        if (reference?.alias) {
            return reference.alias;
        }
        return `ID ${reference?.entityId}`;
    }

    /**
     * German label for the way a dependent uses this table.
     *
     * Reads `usageType` — the backend record has never carried a field named `referenceType`, and reading
     * that name yields `undefined` without any error.
     *
     * @param reference the dependent as returned by the dependency endpoint
     */
    getDependentUsageLabel(reference: LogicDependentReferenceDto): string {
        switch (reference?.usageType) {
            case 'MAPPING_LOGIC':
                return 'Mapping-Logik';
            case 'MAPPING_LOOKUP':
                return 'Flow-Schritt';
            default:
                return reference?.entityType ?? 'Verwendung';
        }
    }

    /**
     * Switches to another navigation section from inside the content area.
     *
     * Used by the withdrawal panel to send the user to the sections that actually carry the `Aktiv` flag.
     *
     * @param action the navigation action to activate
     */
    activateNavigationItem(action: string): void {
        const item = this.navigationItems?.find(navigationItem => navigationItem.action === action);
        if (!item) {
            return;
        }
        this.activeNavigationItem = item;
        this.activeNavigationItemChange.emit(item);
    }

}
